/**
 * Split a filename into basename + extension. Extension includes the leading dot.
 * "report.pdf" -> ["report", ".pdf"]
 * "report"     -> ["report", ""]
 * ".gitignore" -> [".gitignore", ""]
 */
function splitExtension(filename: string): [string, string] {
  const lastDot = filename.lastIndexOf(".");
  if (lastDot <= 0) return [filename, ""];
  return [filename.slice(0, lastDot), filename.slice(lastDot)];
}

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Compute the next available "(N)" suffix for a duplicate upload.
 *
 * Scans `existingFilenames` for any name of the form `basename (N).ext` (same
 * basename + extension as `baseFilename`) and returns the lowest positive N
 * that is not taken. If the original `baseFilename` itself is absent from the
 * existing set, the caller should have short-circuited; this helper always
 * returns a suffix >= 1.
 */
export function nextDuplicateSuffix(baseFilename: string, existingFilenames: string[]): string {
  const [base, ext] = splitExtension(baseFilename);
  const pattern = new RegExp(`^${escapeRegex(base)} \\((\\d+)\\)${escapeRegex(ext)}$`);

  const taken = new Set<number>();
  for (const name of existingFilenames) {
    const match = pattern.exec(name);
    if (match) {
      const n = parseInt(match[1], 10);
      if (Number.isFinite(n) && n >= 1) taken.add(n);
    }
  }

  let n = 1;
  while (taken.has(n)) n += 1;
  return `${base} (${n})${ext}`;
}
